import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import type { ZodTypeAny } from 'zod'
import { api } from '@/services/api'
import type { PagedResponse } from '@/lib/api-params'

// Convention: query keys are the API path split into parts so we can
// invalidate by prefix. `/deals` → ['deals'], `/deals/123` → ['deals','123'],
// `/rfp-checkpoints?rfp_id=xxx` → ['rfp-checkpoints', { rfp_id: 'xxx' }].
export function pathToKey(path: string): readonly unknown[] {
  const [base, qs] = path.split('?')
  const parts = base.replace(/^\//, '').split('/').filter(Boolean)
  if (!qs) return parts
  const params: Record<string, string> = {}
  for (const pair of qs.split('&')) {
    const [k, v = ''] = pair.split('=')
    params[decodeURIComponent(k)] = decodeURIComponent(v)
  }
  return [...parts, params]
}

/** GET wrapper. Returns React Query's query state.
 *
 * Pass `schema` (a zod schema) to validate the response at the fetch
 * boundary. On parse failure we log a clear ZodError (with field paths)
 * to console + Sentry-style telemetry and throw — the route's error
 * boundary catches it and shows a recoverable fallback instead of
 * letting the bad shape propagate to render. Without `schema` the
 * response is trusted as before.
 */
export function useApiGet<T>(
  path: string,
  opts?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> & { schema?: ZodTypeAny },
) {
  const { schema, ...rest } = opts ?? {}
  return useQuery<T>({
    queryKey: pathToKey(path),
    queryFn: async () => {
      const raw = await api.get<unknown>(path)
      if (!schema) return raw as T
      const parsed = schema.safeParse(raw)
      if (!parsed.success) {
        // Surfaces as a clear, actionable message: "GET /views response
        // mismatch: config.filters expected array, received string"
        const firstIssue = parsed.error.issues[0]
        const where = firstIssue?.path?.join('.') ?? 'response'
        const msg = `GET ${path} response mismatch at ${where}: ${firstIssue?.message ?? 'invalid'}`
        console.error('[api.zod]', msg, parsed.error.issues)
        throw new Error(msg)
      }
      return parsed.data as T
    },
    ...rest,
  })
}

/** Every page of a paginated list endpoint, as one array.
 *
 * `path` carries the filters (no page / page_size). Pages are fetched in
 * sequence at `pageSize` until the server's `pages` is reached, so a list is
 * complete however long it is; `maxPages` is only a runaway guard. The query
 * key is the first page's path, so invalidating the list prefix (e.g.
 * pathToKey('/contacts')) refetches this too.
 */
export function useApiGetAllPages<T>(
  path: string,
  opts?: { enabled?: boolean; pageSize?: number; maxPages?: number },
) {
  const pageSize = opts?.pageSize ?? 200
  const maxPages = opts?.maxPages ?? 50
  const sep = path.includes('?') ? '&' : '?'
  const pageUrl = (page: number) => `${path}${sep}page=${page}&page_size=${pageSize}`
  return useQuery<T[]>({
    queryKey: [...pathToKey(pageUrl(1)), 'all-pages'],
    queryFn: async () => {
      const first = await api.get<PagedResponse<T>>(pageUrl(1))
      const items = [...(first.items ?? [])]
      const last = Math.min(first.pages ?? 1, maxPages)
      for (let page = 2; page <= last; page += 1) {
        const next = await api.get<PagedResponse<T>>(pageUrl(page))
        items.push(...(next.items ?? []))
      }
      return items
    },
    enabled: opts?.enabled,
  })
}

/** POST mutation. onSuccess invalidates the listed paths. */
export function useApiPost<T, B = unknown>(
  path: string,
  opts?: { invalidate?: string[] },
) {
  const qc = useQueryClient()
  return useMutation<T, Error, B>({
    mutationFn: (body) => api.post<T>(path, body),
    onSuccess: () => {
      for (const p of opts?.invalidate ?? [path]) {
        void qc.invalidateQueries({ queryKey: pathToKey(p) })
      }
    },
  })
}

/** PATCH mutation. Takes `{ id, body }` so callers can target any sub-path. */
export function useApiPatch<T, B = unknown>(
  basePath: string,
  opts?: { invalidate?: string[] },
) {
  const qc = useQueryClient()
  return useMutation<T, Error, { id: string; body: B }>({
    mutationFn: ({ id, body }) => api.patch<T>(`${basePath}/${id}`, body),
    onSuccess: () => {
      for (const p of opts?.invalidate ?? [basePath]) {
        void qc.invalidateQueries({ queryKey: pathToKey(p) })
      }
    },
  })
}

/** DELETE mutation. */
export function useApiDelete(
  basePath: string,
  opts?: { invalidate?: string[] },
) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`${basePath}/${id}`),
    onSuccess: () => {
      for (const p of opts?.invalidate ?? [basePath]) {
        void qc.invalidateQueries({ queryKey: pathToKey(p) })
      }
    },
  })
}
